import { useContext, useEffect, useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DefaultButton, Spinner, SpinnerSize } from '@fluentui/react'

import { getClientList, getClientDetail, toggleClientCommunication, ClientSummary, ClientDetail } from '../../api'
import { AppStateContext } from '../../state/AppProvider'

import styles from './Clients.module.css'

const PAGE_SIZE_OPTIONS = [200, 500]

const Clients = () => {
  const navigate = useNavigate()
  const appStateContext = useContext(AppStateContext)
  const [clients, setClients] = useState<ClientSummary[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [filterIds, setFilterIds] = useState<number[] | null>(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(200)
  const allClientsRef = useRef<ClientSummary[]>([])

  const [view, setView] = useState<'list' | 'detail'>('list')
  const [selectedClient, setSelectedClient] = useState<ClientDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [commToggling, setCommToggling] = useState(false)

  const loadClients = useCallback(async (p: number, ps: number) => {
    setLoading(true)
    const data = await getClientList(ps, p * ps)
    allClientsRef.current = data.items
    setClients(data.items)
    setTotal(data.total)
    setLoading(false)
  }, [])

  useEffect(() => {
    loadClients(page, pageSize)
  }, [page, pageSize])

  useEffect(() => {
    const handler = () => { loadClients(page, pageSize) }
    window.addEventListener('app-data-changed', handler)
    return () => window.removeEventListener('app-data-changed', handler)
  }, [loadClients, page, pageSize])

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.type === 'clients' && detail?.ids) {
        setFilterIds(detail.ids)
      }
    }
    window.addEventListener('app-grid-filter', handler)
    return () => window.removeEventListener('app-grid-filter', handler)
  }, [])

  const displayed = filterIds
    ? allClientsRef.current.filter(c => filterIds.includes(c.id))
    : clients

  const totalPages = Math.ceil(total / pageSize)

  useEffect(() => {
    let context = `User is viewing the Clients page.`
    if (view === 'detail' && selectedClient) {
      context += ` Viewing detail for client ${selectedClient.company_name} (ID: ${selectedClient.id}).`
    } else if (filterIds) {
      context += ` Showing ${displayed.length} filtered clients (from chatbot search). The grid is filtered by the chatbot search results.`
    } else if (clients.length > 0) {
      context += ` ${clients.length} clients listed (page ${page + 1} of ${totalPages}, ${total} total).`
    }
    appStateContext?.dispatch({ type: 'SET_SCREEN_CONTEXT', payload: context })
    return () => { appStateContext?.dispatch({ type: 'SET_SCREEN_CONTEXT', payload: '' }) }
  }, [clients.length, filterIds, displayed.length, page, totalPages, total, view, selectedClient])

  const clearFilter = () => {
    setFilterIds(null)
  }

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value))
    setPage(0)
    setFilterIds(null)
  }

  const handleClientClick = async (id: number) => {
    setDetailLoading(true)
    setView('detail')
    const detail = await getClientDetail(id)
    setSelectedClient(detail)
    setDetailLoading(false)
  }

  const handleBackToList = () => {
    setView('list')
    setSelectedClient(null)
  }

  const handleToggleCommunication = async () => {
    if (!selectedClient || commToggling) return
    let reason = ''
    // Prompt for reason when disabling
    if (selectedClient.send_communication !== false) {
      const input = window.prompt('Reason for disabling communications:')
      if (input === null) return // cancelled
      reason = input
    }
    setCommToggling(true)
    const result = await toggleClientCommunication(selectedClient.id, reason)
    if (result !== null) {
      setSelectedClient({ ...selectedClient, send_communication: result.send_communication, send_communication_reason: result.send_communication_reason })
      // Update list data too
      allClientsRef.current = allClientsRef.current.map(c =>
        c.id === selectedClient.id ? { ...c, send_communication: result.send_communication } : c
      )
      setClients(prev => prev.map(c =>
        c.id === selectedClient.id ? { ...c, send_communication: result.send_communication } : c
      ))
    }
    setCommToggling(false)
  }

  const formatValue = (val: any): string => {
    if (val === null || val === undefined || val === 'N/A') return '-'
    if (typeof val === 'object') return JSON.stringify(val)
    return String(val)
  }

  if (view === 'detail') {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <DefaultButton
            className={styles.backButton}
            iconProps={{ iconName: 'Back' }}
            onClick={handleBackToList}
            title="Back to list"
          />
          <h1 className={styles.title}>Client Detail</h1>
        </div>
        {detailLoading ? (
          <div className={styles.loading}>
            <Spinner size={SpinnerSize.large} label="Loading client..." />
          </div>
        ) : !selectedClient ? (
          <div className={styles.emptyState}>Client not found.</div>
        ) : (
          <div className={styles.detailCard}>
            <div className={styles.detailHeader}>
              <h2 className={styles.detailName}>{selectedClient.company_name}</h2>
              <button
                className={`${styles.commToggleButton} ${selectedClient.send_communication === false ? styles.commDisabled : styles.commEnabled}`}
                onClick={handleToggleCommunication}
                disabled={commToggling}
              >
                {commToggling ? '...' : selectedClient.send_communication === false ? 'Comms Disabled' : 'Comms Enabled'}
              </button>
            </div>
            {selectedClient.send_communication === false && (
              <div className={styles.commReasonBar}>
                <strong>Comms Disabled</strong> — {selectedClient.send_communication_reason || 'No reason provided'}
              </div>
            )}
            <div className={styles.detailGrid}>
              {[
                ['ID', selectedClient.id],
                ['Industry', selectedClient.industry],
                ['Contact Person', selectedClient.contact_person],
                ['Email', selectedClient.email],
                ['Phone', selectedClient.phone],
                ['Address', selectedClient.address],
                ['Website', selectedClient.website],
                ['Notes', selectedClient.notes],
                ['Created', selectedClient.created],
                ['Modified', selectedClient.modified],
              ].map(([label, value]) => (
                <div className={styles.detailRow} key={String(label)}>
                  <span className={styles.detailLabel}>{String(label)}</span>
                  <span className={styles.detailValue}>{formatValue(value)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <DefaultButton
          className={styles.backButton}
          iconProps={{ iconName: 'Back' }}
          onClick={() => navigate('/')}
          title="Back to chat"
        />
        <h1 className={styles.title}>Clients</h1>
        {filterIds && (
          <div className={styles.filterBar}>
            <span className={styles.filterLabel}>Filtered: {displayed.length} of {allClientsRef.current.length}</span>
            <DefaultButton
              className={styles.clearFilterButton}
              text="Show All"
              onClick={clearFilter}
            />
          </div>
        )}
      </div>
      {loading ? (
        <div className={styles.loading}>
          <Spinner size={SpinnerSize.large} label="Loading clients..." />
        </div>
      ) : displayed.length === 0 ? (
        <div className={styles.emptyState}>
          {filterIds ? 'No clients match the current filter.' : 'No clients found.'}
          {filterIds && (
            <DefaultButton
              className={styles.clearFilterButton}
              text="Show All"
              onClick={clearFilter}
              style={{ marginTop: 12 }}
            />
          )}
        </div>
      ) : (
        <>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Company</th>
                <th>Industry</th>
                <th>Contact</th>
                <th>Email</th>
                <th>Phone</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map(c => (
                <tr key={c.id}>
                  <td>{c.id}</td>
                  <td>
                    <span className={styles.clickableLink} onClick={() => handleClientClick(c.id)}>
                      {c.company_name !== 'N/A' ? c.company_name : '-'}
                    </span>
                    {c.send_communication === false && <span className={styles.noCommsBadge}>No Comms</span>}
                  </td>
                  <td>{c.industry !== 'N/A' ? c.industry : '-'}</td>
                  <td>{c.contact_person !== 'N/A' ? c.contact_person : '-'}</td>
                  <td>{c.email !== 'N/A' ? c.email : '-'}</td>
                  <td>{c.phone !== 'N/A' ? c.phone : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!filterIds && (
            <div className={styles.pagination}>
              <DefaultButton
                className={styles.pageButton}
                text="Previous"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              />
              <span className={styles.pageInfo}>
                Page {page + 1} of {totalPages} ({total} total)
              </span>
              <DefaultButton
                className={styles.pageButton}
                text="Next"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage(p => p + 1)}
              />
              <select className={styles.pageSizeSelect} value={pageSize} onChange={handlePageSizeChange}>
                {PAGE_SIZE_OPTIONS.map(s => (
                  <option key={s} value={s}>{s} per page</option>
                ))}
              </select>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default Clients
